import React, { useEffect, useState } from "react";
import { ArrowRight, Lightbulb, Shield, Car, Layers } from "lucide-react";
import { api } from "@/lib/api";

/**
 * "Popular Protection Options" — the four most-booked standalone PPF services,
 * shown at the top of the PPF section. Prices come live from /api/ppf/config
 * (server-authoritative; never hardcoded). Selecting a card updates the
 * existing PPF calculator below (custom event picked up by Calculator),
 * never a disconnected flow.
 */

const money0 = (n) => (Number.isFinite(Number(n)) ? `$${Math.round(Number(n)).toLocaleString("en-CA")}` : "");

// "From $X" — the D1 (lowest) class price of a package. Undefined when there
// is no real price (avoids NaN while the config loads).
const pkgFrom = (p) => {
  if (p.prices && Object.keys(p.prices).length) return Math.min(...Object.values(p.prices));
  return p.quote_only ? p.from_price : p.price;
};

const CARDS = [
  {
    key: "headlights",
    icon: Lightbulb,
    title: "Headlight Protection",
    desc: "Both headlights in self-healing 7.5 mil film — no more sandblasted lenses.",
    preselect: { extra_parts: [{ key: "headlights", side: "both" }] },
    price: (cfg) => {
      const p = cfg?.parts?.find((x) => x.key === "headlights");
      return p ? `${money0(p.pair)} both` : null;
    },
  },
  {
    key: "windshield",
    icon: Shield,
    title: "Windshield Protection",
    desc: "6.5 mil clear windshield film (Veltik SEC-H WPF) — hydrophobic, high optical clarity.",
    preselect: { windshield: true },
    price: (cfg) => {
      const vals = Object.values(cfg?.windshield?.prices || {}).filter((v) => v != null);
      return vals.length ? `from ${money0(Math.min(...vals))}` : null;
    },
  },
  {
    key: "partial-front",
    icon: Car,
    title: "Partial Front PPF",
    desc: "Bumper, first 24″ of the hood, partial front fenders, headlights and mirrors.",
    preselect: { package: "partial-front" },
    price: (cfg) => {
      const p = cfg?.packages?.find((x) => x.key === "partial-front");
      const v = p ? pkgFrom(p) : null;
      return Number.isFinite(v) && v > 0 ? `from ${money0(v)}` : null;
    },
  },
  {
    key: "rockers",
    icon: Layers,
    title: "Rocker Panel Protection",
    desc: "Lower side panels — the strip that takes the most road rash and winter sand.",
    preselect: { extra_parts: [{ key: "rocker-panels", side: "both" }] },
    price: (cfg) => {
      const p = cfg?.parts?.find((x) => x.key === "rocker-panels");
      return p ? `${money0(p.pair)} both` : null;
    },
  },
];

export default function PpfPopular({ testid = "ppf-popular" }) {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/ppf/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const choose = (card) => {
    try {
      window.dispatchEvent(new CustomEvent("wtd-ppf-preselect", { detail: card.preselect }));
    } catch { /* older browsers — scroll still works */ }
    document.getElementById("calculator")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="mb-10" data-testid={testid}>
      <div className="font-mono-alt text-[10px] uppercase tracking-widest text-[#8db2ff] mb-2">Start here</div>
      <h2 className="font-display text-3xl md:text-4xl mb-2">Popular Protection Options</h2>
      <p className="text-sm text-white/50 mb-5 max-w-2xl">
        The four services most Edmonton drivers start with. Tap one — the PPF calculator below opens with it pre-selected.
      </p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        {CARDS.map((c) => {
          const price = c.price(cfg);
          const Icon = c.icon;
          return (
            <button key={c.key} type="button" onClick={() => choose(c)}
              data-testid={`${testid}-${c.key}`}
              className="group text-left p-4 rounded-xl border border-white/10 bg-black/30 hover:border-[#1F5EFF]/60 hover:bg-[#1F5EFF]/10 transition-colors flex flex-col">
              <Icon className="w-5 h-5 text-[#8db2ff] mb-2.5" />
              <div className="text-white font-medium text-sm md:text-base leading-tight">{c.title}</div>
              <div className="text-[11px] text-white/45 mt-1.5 leading-snug flex-1">{c.desc}</div>
              <div className="mt-3 flex items-center justify-between gap-2">
                <span className="text-white font-semibold tabular-nums text-sm" data-testid={`${testid}-price-${c.key}`}>
                  {price || "…"}
                </span>
                <span className="inline-flex items-center gap-1 text-[10px] font-mono-alt uppercase tracking-widest text-[#8db2ff] group-hover:text-white transition-colors">
                  Select <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
